import React from 'react';
import type { ComparisonItem } from './types';

interface Props {
  comparison:      ComparisonItem[];
  prevPeriodLabel: string;
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

function TrendChip({ pct }: { pct: number | null }) {
  if (pct === null) return <span style={styles.noData}>No prev. data</span>;

  const improved = pct < 0;
  const neutral  = pct === 0;

  const bg    = neutral ? 'rgba(128,128,128,0.12)' : improved ? '#E1F5EE' : '#FAEEDA';
  const color = neutral ? '#888780'                : improved ? '#0F6E56' : '#993C1D';
  const arrow = neutral ? '→' : improved ? '↓' : '↑';

  return (
    <span style={{ ...styles.chip, background: bg, color }}>
      {arrow} {Math.abs(pct)}%
    </span>
  );
}

function ComparisonRow({ item, prevPeriodLabel }: { item: ComparisonItem; prevPeriodLabel: string }) {
  const pct      = pctChange(item.current, item.previous);
  const fmt      = (v: number) => `${item.prefix ?? ''}${v.toLocaleString('en-GB')} ${item.unit}`.trim();
  const barWidth = item.previous > 0
    ? Math.min(Math.round((item.current / Math.max(item.current, item.previous)) * 100), 100)
    : 100;
  const prevBarWidth = item.previous > 0
    ? Math.min(Math.round((item.previous / Math.max(item.current, item.previous)) * 100), 100)
    : 0;

  return (
    <div style={styles.row}>
      <div style={styles.rowHeader}>
        <div style={styles.rowLeft}>
          <span style={{ ...styles.colorDot, background: item.color }} />
          <span style={styles.rowLabel}>{item.label}</span>
        </div>
        <TrendChip pct={pct} />
      </div>

      {/* Current period bar */}
      <div style={styles.barGroup}>
        <span style={styles.barLabel}>Now</span>
        <div style={styles.barTrack}>
          <div style={{ ...styles.barFill, width: `${barWidth}%`, background: item.color }} />
        </div>
        <span style={styles.barValue}>{fmt(item.current)}</span>
      </div>

      {/* Previous period bar */}
      <div style={styles.barGroup}>
        <span style={styles.barLabel}>{prevPeriodLabel.replace('vs ', '')}</span>
        <div style={styles.barTrack}>
          <div style={{ ...styles.barFill, width: `${prevBarWidth}%`, background: item.color, opacity: 0.3 }} />
        </div>
        <span style={{ ...styles.barValue, opacity: 0.6 }}>{fmt(item.previous)}</span>
      </div>
    </div>
  );
}

export default function TargetsPanel({ comparison, prevPeriodLabel }: Props) {
  return (
    <div style={styles.panel}>
      <p style={styles.title}>Period comparison</p>
      <p style={styles.sub}>{prevPeriodLabel}</p>

      {!comparison.length ? (
        <p style={styles.empty}>No data available</p>
      ) : (
        <div style={styles.list}>
          {comparison.map(item => (
            <ComparisonRow key={item.id} item={item} prevPeriodLabel={prevPeriodLabel} />
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    background:   'var(--color-bg-secondary, #f5f5f3)',
    borderRadius: 12,
    padding:      '1.1rem 1.25rem',
    fontFamily:   "'Syne', sans-serif",
  },
  title: {
    fontSize:   13,
    fontWeight: 600,
    color:      'var(--color-text-primary, #2C2C2A)',
    margin:     '0 0 0.15rem',
  },
  sub: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   11,
    color:      'var(--color-text-secondary, #5F5E5A)',
    margin:     '0 0 1rem',
    textTransform: 'capitalize',
  },
  list: {
    display:       'flex',
    flexDirection: 'column',
    gap:           16,
  },
  row: {},
  rowHeader: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    marginBottom:   6,
  },
  rowLeft: {
    display:    'flex',
    alignItems: 'center',
    gap:        6,
  },
  colorDot: {
    width:        8,
    height:       8,
    borderRadius: '50%',
    flexShrink:   0,
  },
  rowLabel: {
    fontSize:   12,
    fontWeight: 500,
    color:      'var(--color-text-primary, #2C2C2A)',
  },
  chip: {
    fontFamily:   "'DM Mono', monospace",
    fontSize:     11,
    fontWeight:   600,
    padding:      '2px 7px',
    borderRadius: 20,
  },
  barGroup: {
    display:    'flex',
    alignItems: 'center',
    gap:        8,
    marginBottom: 3,
  },
  barLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   10,
    color:      'var(--color-text-secondary, #5F5E5A)',
    width:      48,
    flexShrink: 0,
    textAlign:  'right',
  },
  barTrack: {
    flex:         1,
    height:       5,
    background:   'rgba(128,128,128,0.12)',
    borderRadius: 3,
    overflow:     'hidden',
  },
  barFill: {
    height:       '100%',
    borderRadius: 3,
    transition:   'width 0.6s cubic-bezier(0.4,0,0.2,1)',
  },
  barValue: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   11,
    color:      'var(--color-text-primary, #2C2C2A)',
    width:      80,
    flexShrink: 0,
    textAlign:  'right',
  },
  noData: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   10,
    color:      'var(--color-text-secondary, #5F5E5A)',
  },
  empty: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   12,
    color:      'var(--color-text-secondary, #5F5E5A)',
    margin:     0,
  },
};
